import {useMutation, UseMutationResult} from '@tanstack/react-query';
import {useLocale} from 'state/locale';
import Api from 'types/api';
import {request} from 'utils/request';

export type ExportSuccessResponse = Api.Response.Success<never>;

export type ExportErrorResponse = Api.Response.Failure<{
    string: boolean;
}>;

export enum ClientInboxItemState {
    NOT_VIEWED = 'not_viewed',
    VIEWED = 'viewed',
}

export type ClientInboxDocument = {
    addressee: string;
    amountToPay?: string;
    dateCancelled?: string;
    dateSent: string;
    displayName: string;
    documentNumber?: string;
    documentType: string;
    documentTypeName: string;
    filename: string;
    fileSize: string;
    isCancelled: boolean;
    isInaccessible: boolean;
    isInfoOverlayOpen?: boolean;
    message: string;
    mid: string;
    moneyRequest: {
        cancelled: boolean;
        paid: boolean;
    } | null;
    name: string;
    paid?: boolean;
    projectName?: string;
    selected?: boolean;
    state: ClientInboxItemState;
    total: string;
    url: string;
};

export type PersonalInboxFormFields = {
    pickupAuthorizations: string[];
    selectAll: boolean;
};

export type ClientInboxFormFields = {
    clientinboxItems: string[];
    selectAll: boolean;
};

export const usePostDownloadPickupItems = (): UseMutationResult<
    ExportSuccessResponse,
    ExportErrorResponse,
    {
        formData: PersonalInboxFormFields;
        portalMid: string;
    }
> => {
    const locale = useLocale();

    return useMutation(({formData, portalMid}) =>
        request({
            formData,
            locale,
            method: 'POST',
            uri: `/portal/${portalMid}/download/`,
        }),
    );
};

export const usePostDownloadClientInboxItems = (): UseMutationResult<
    ExportSuccessResponse,
    ExportErrorResponse,
    {
        formData: ClientInboxFormFields;
        portalMid: string;
    }
> => {
    const locale = useLocale();

    return useMutation(({formData, portalMid}) =>
        request({
            formData,
            locale,
            method: 'POST',
            uri: `/portal/${portalMid}/client-inbox/download/`,
        }),
    );
};

export type ClientInboxItemPdfSuccessResponse = Api.Response.Success<BlobPart>;

export type ClientInboxItemPdfErrorResponse = Api.Response.Failure<'value'>;

export const useGetClientInboxItemPdf = (): UseMutationResult<
    ClientInboxItemPdfSuccessResponse,
    ClientInboxItemPdfErrorResponse,
    {
        documentMid: string;
        portalMid: string;
    }
> => {
    const locale = useLocale();

    return useMutation(({documentMid, portalMid}) =>
        request({
            locale,
            method: 'GET',
            responseType: 'arraybuffer',
            uri: `/portal/${portalMid}/client-inbox/file-download/${documentMid}/`,
        }),
    );
};

export type AcceptOrderSuccessResponse = Api.Response.Success<never>;

export type AcceptOrderErrorResponse = Api.Response.Failure<{
    string: boolean;
}>;

export type AcceptOrderFormData = {
    note: string;
    signer: string;
};

export const usePostAcceptOrder = (): UseMutationResult<
    AcceptOrderSuccessResponse,
    AcceptOrderErrorResponse,
    {
        documentMid: string;
        formData: AcceptOrderFormData;
        portalMid: string;
    }
> => {
    const locale = useLocale();

    return useMutation(({documentMid, formData, portalMid}) =>
        request({
            formData,
            locale,
            method: 'POST',
            uri: `/portal/${portalMid}/client-inbox/accept/${documentMid}/`,
        }),
    );
};

export type ReceiveDocumentSuccessResponse = Api.Response.Success<never>;

export type ReceiveDocumentErrorResponse = Api.Response.Failure<{
    string: boolean;
}>;

export type ReceiveDocumentFormData = {
    receivingPartner: string;
};

export const usePostReceiveDocument = (): UseMutationResult<
    ReceiveDocumentSuccessResponse,
    ReceiveDocumentErrorResponse,
    {
        documentMid: string;
        formData: ReceiveDocumentFormData;
        portalMid: string;
    }
> => {
    const locale = useLocale();

    return useMutation(({documentMid, formData, portalMid}) =>
        request({
            formData,
            locale,
            method: 'POST',
            uri: `/portal/${portalMid}/client-inbox/receive/${documentMid}/`,
        }),
    );
};
